import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { Blog } from './types'
import BlogCard from './BlogCard'
import { APP_ROUTES } from '@/common/constants'
import { useFetchBlogs } from './blogQueries'
import { ChevronRight } from 'lucide-react'
import { collectCurrentCategories, isPrimaryRelated, sortByDateDesc } from './blogUtils'
import { Button } from '@/common/ui/button'
import { H2 } from '@/common/ui/h2'

function buildRelated(source: Blog[], current: Blog | undefined, limit: number): Blog[] {
    const src = source || []
    if (!current) return src.slice(0, limit)

    const cats = collectCurrentCategories(current)

    const primary = src.filter((b) => isPrimaryRelated(b, current, cats))
    const primarySorted = sortByDateDesc(primary)

    const result: Blog[] = []
    const added = new Set<string>()

    for (const p of primarySorted) {
        if (result.length >= limit) break
        result.push(p)
        added.add(p.blogId)
    }

    if (result.length < limit) {
        const remaining = src.filter((b) => b && b.blogId !== current.blogId && !added.has(b.blogId))
        const remainingSorted = sortByDateDesc(remaining)
        for (const r of remainingSorted) {
            if (result.length >= limit) break
            result.push(r)
            added.add(r.blogId)
        }
    }

    return result.slice(0, limit)
}
export default function MoreBlogs({
    title,
    current,
    blogs,
    limit = 3,
}: {
    title: string
    current?: Blog
    blogs?: Blog[]
    limit?: number
}): JSX.Element {
    const { data } = useFetchBlogs(blogs === undefined)
    const navigate = useNavigate()

    const related = useMemo(() => {
        const source = blogs ?? data?.blogs ?? []
        return buildRelated(source, current, limit)
    }, [blogs, data?.blogs, current, limit])

    return (
        <div className="self-stretch inline-flex flex-col justify-start items-start gap-6">
            <div className="self-stretch flex flex-col justify-start items-start gap-4">
                <Button
                    variant="link"
                    onClick={() => navigate(APP_ROUTES.BLOGS)}
                    className="self-stretch inline-flex justify-start items-center gap-2 hover:no-underline px-0"
                >
                    <H2 className="justify-center text-black text-3xl font-normal font-['Serious Sans'] leading-10 mb-0">
                        {title}
                    </H2>
                    <ChevronRight className="text-content-tertiary w-6 h-6" />
                </Button>
            </div>
            <div className="self-stretch inline-flex justify-start items-start gap-6 overflow-x-auto flex-nowrap">
                {related.map((r) => (
                    <div className="flex-1 min-w-[240px]" key={r.blogId}>
                        <BlogCard blog={r} />
                    </div>
                ))}
            </div>
        </div>
    )
}
